import Table from '../../../../common/table'
import { SearchQuery, SearchResult, SearchTemplate, SearchView } from '../../../../generic-search-common/src'
import ExportSearchResults from '../export-search-results'
import { DISPLAY_DATE_FORMAT } from '../../../../../../services/constants'
import {
  ActionsMenu,
  Button,
  ButtonsContainer,
  Card,
  ColumnHeader,
  ConditionalCardWrapper,
  DropdownMenu,
  HeadingRow,
  TableAction,
  TableSortByProps,
} from '@msaf/core-react'

export interface ExportConfigProps {
  id: string
  label: string
  url: string
  csrfToken?: string
  viewKey: string
  exportFileName?: string
}

export interface ResultsProps {
  isSearching?: boolean
  currentView: SearchView
  searchQuery: SearchQuery
  searchResults: SearchResult
  setCurrentPage: (page: number) => void
  setSortColumn: (orderColumn: string, orderDirection: 'asc' | 'desc') => void
  customActions?: { [key: string]: (...args: any[]) => void }
  searchTemplate: SearchTemplate
  currentUserPermissions?: Array<string>
  isShowResultsOnMap?: boolean
  defaultSortBy: TableSortByProps
  heading?: string
  isShowResultsCard?: boolean
  exportConfig?: Array<ExportConfigProps>
  resultNameSingular?: string
  resultNamePlural?: string
  showResultsCount?: boolean
  pageSize?: number
  dateFormat?: string
}

export function Results({
  searchTemplate,
  currentView,
  searchResults,
  searchQuery,
  setCurrentPage,
  setSortColumn,
  customActions,
  currentUserPermissions,
  isShowResultsOnMap = true,
  defaultSortBy,
  heading,
  isShowResultsCard = true,
  exportConfig,
  resultNameSingular,
  resultNamePlural,
  showResultsCount,
  pageSize = 10,
  dateFormat = DISPLAY_DATE_FORMAT,
}: ResultsProps) {
  return (
    <ConditionalCardWrapper condition={isShowResultsCard} wrapper={(children) => <Card isFullWidth>{children}</Card>}>
      <>
        {/* Heading defaults to 'Results' but can be removed by passing in empty string. TODO: improve this logic */}
        {(heading === undefined || isShowResultsOnMap || exportConfig) && (
          <HeadingRow headingLevel={2} headingText={heading ?? 'Results'}>
            {(isShowResultsOnMap || exportConfig) && (
              <ButtonsContainer containerStyle='right'>
                {isShowResultsOnMap && (
                  <Button
                    label='Show results on map'
                    icon='map-pin'
                    iconAlignment='left'
                    iconAriaLabel='Show results on map'
                    path={searchTemplate.mapConfig?.mapViewEmberRoute}
                  />
                )}
                {exportConfig && (
                  <ActionsMenu label='Export' buttonStyle='secondary' isLeftAligned={false}>
                    <DropdownMenu>
                      {exportConfig.map((config: ExportConfigProps) => (
                        <ExportSearchResults
                          key={config.id}
                          actionUrl={config.url}
                          csrfToken={config.csrfToken}
                          actionQuery={JSON.stringify({
                            searchQuery: {
                              ...searchQuery,
                              // Overwrite the ordering here to ensure export ordering is not overwritten by cached search query.
                              // Ordering is set by the server for the export views.
                              ordering: [],
                              requestedExport: true,
                              viewKey: config.viewKey,
                              exportFileName: config.exportFileName,
                            },
                          })}
                          actionLabel={config.label}
                        />
                      ))}
                    </DropdownMenu>
                  </ActionsMenu>
                )}
              </ButtonsContainer>
            )}
          </HeadingRow>
        )}
        <Table
          columnHeaders={currentView.table.columns as Array<ColumnHeader>}
          tableData={searchResults.rows || []}
          onRowClick={currentView.table.onRowClick as Omit<TableAction, 'handler' | 'actionPermissions'>}
          defaultSortBy={defaultSortBy}
          setSortColumn={setSortColumn}
          customActions={customActions}
          currentUserPermissions={currentUserPermissions}
          currentPage={searchQuery?.requestedPage ?? 1}
          setPage={setCurrentPage}
          totalResults={searchResults?.totalRows ?? 0}
          totalPages={searchResults?.totalRows ? Math.ceil(searchResults.totalRows / pageSize) : 0}
          resultNameSingular={resultNameSingular}
          resultNamePlural={resultNamePlural}
          showResultsCount={showResultsCount}
          dateFormat={dateFormat}
        />
      </>
    </ConditionalCardWrapper>
  )
}
